import * as React from 'react';
import {useEffect, useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {wrap} from 'popmotion';
import {Box, Text, Image, useMediaQuery} from '@chakra-ui/react';
import {FiChevronLeft, FiChevronRight} from 'react-icons/fi';

const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 200 : -200,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 200 : -200,
      opacity: 0,
    };
  },
};

/**
 * Experimenting with distilling swipe offset and velocity into a single variable, so the
 * less distance a user has swiped, the more velocity they need to register as a swipe.
 * Should accomodate longer swipes and short flicks without having binary checks on
 * just distance thresholds and velocity > 0.
 */
const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

interface CarouselProps {
  images: string[];
}

export const Carousel: React.FC<CarouselProps> = ({images}) => {
  const [[page, direction], setPage] = useState([0, 0]);
  const [activeTimeout, setTimeoutFunction] = useState<NodeJS.Timeout>();
  const [isLargerThan992] = useMediaQuery('(min-width: 992px)');

  // const [imageIndex, setImageIndex] = useState<number>(
  //   wrap(0, images.length, page),
  // );

  // We only have 3 images, but we paginate them absolutely (ie 1, 2, 3, 4, 5...) and
  // then wrap that within 0-2 to find our image ID in the array below. By passing an
  // absolute page index as the `motion` component's `key` prop, `AnimatePresence` will
  // detect it as an entirely new image. So you can infinitely paginate as few as 1 images.
  const imageIndex = wrap(0, images.length, page);

  const paginate = (newDirection: number) => {
    if (activeTimeout) clearTimeout(activeTimeout);
    setPage([page + newDirection, newDirection]);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage([imageIndex + 1, -1]);
    }, 5000);

    setTimeoutFunction(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="row"
      h={{base: '200px', md: '400px', lg: '500px'}}
      userSelect="none"
      justifyContent="center"
    >
      <Box
        display="flex"
        flexGrow={1}
        flexWrap="nowrap"
        position="relative"
        justifyContent="center"
        maxWidth={{base: '320px', md: '640px', lg: '800px'}}
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.img
            style={{
              position: 'absolute',
              top: 1,
              left: 1,
              // eslint-disable-next-line no-nested-ternary
              height: '100%',
              objectFit: 'contain',
            }}
            className="carousel-image"
            key={page}
            src={images[imageIndex]}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: {type: 'spring', stiffness: 300, damping: 30},
              opacity: {duration: 0.2},
            }}
            drag="x"
            dragConstraints={{left: 0, right: 0}}
            dragElastic={1}
            onDragEnd={(e, {offset, velocity}) => {
              const swipe = swipePower(offset.x, velocity.x);

              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
            alt={`carousel image ${imageIndex + 1}`}
          />
        </AnimatePresence>
        <Box
          zIndex="9"
          position="absolute"
          bottom={{base: '20px', md: '50px', lg: '30px'}}
          right="10px"
          color="black"
        >
          <Text textShadow="-1px -1px 0 #FFFFFF, 1px -1px 0 #FFFFFF, -1px 1px 0 #FFFFFF, 1px 1px 0 #FFFFFF">
            {imageIndex + 1} / {images.length}
          </Text>
        </Box>
        <Box
          className="prev"
          onClick={() => paginate(-1)}
          position="absolute"
          zIndex="2"
          h="40px"
          w="40px"
          fontSize="30px"
          top="calc(50% - 20px)"
          left="10px"
          // transform="scale(-1)"
          textAlign="center"
          cursor="pointer"
          backgroundColor="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="50%"
          border="3px solid black"
        >
          <FiChevronLeft />
        </Box>
        <Box
          className="next"
          onClick={() => paginate(1)}
          position="absolute"
          zIndex="2"
          h="40px"
          w="40px"
          fontSize="30px"
          top="calc(50% - 20px)"
          right="10px"
          textAlign="center"
          cursor="pointer"
          backgroundColor="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="50%"
          border="3px solid black"
        >
          <FiChevronRight />
        </Box>
      </Box>
      {isLargerThan992 && (
        <Box
          zIndex={3}
          height={{md: '400px', lg: '500px'}}
          display="flex"
          flexGrow={0}
          flexShrink={1}
          flexBasis={(500 / images.length) * 1.6}
          flexDirection="column"
          marginTop="1px"
        >
          {images.map((image: string, index: number) => (
            <Box
              backgroundColor="black"
              cursor="pointer"
              minWidth={0}
              onClick={() => {
                if (activeTimeout) clearTimeout(activeTimeout);
                setPage([index, -1]);
              }}
            >
              <Image
                src={image}
                opacity={index !== imageIndex ? 0.5 : 1}
                alt={`image ${index + 1} thumbnail`}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
